/** 护工工单履约状态机 — 一期纯勾选版 */

export const TASK_PHASE = {
  DEPARTING: "departing",
  ENROUTE: "enroute",
  SERVING: "serving",
};

export const PROGRESS_STEPS = [
  { key: "depart", label: "前往现场" },
  { key: "arrive", label: "我已到达" },
  { key: "start", label: "开始工作" },
  { key: "log", label: "录入日志" },
  { key: "finish", label: "申请完工" },
];

const GEOFENCE_METERS = 500;

function pad(n) {
  return String(n).padStart(2, "0");
}

function formatDateKey(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDisplayDate(dateKey) {
  const [y, m, d] = dateKey.split("-");
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  const weekdays = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
  return `${Number(m)}月${Number(d)}日 ${weekdays[dt.getDay()]}`;
}

function parseDateKey(dateKey) {
  const [y, m, d] = dateKey.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function eachServiceDate(startDate, endDate) {
  const start = parseDateKey(startDate);
  const end = parseDateKey(endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cap = end < today ? end : today;
  const dates = [];
  const cur = new Date(start);
  cur.setHours(0, 0, 0, 0);
  while (cur <= cap) {
    dates.push(formatDateKey(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

function defaultLogEntry() {
  return {
    fed: null,
    turned: null,
    mood: null,
    submittedAt: null,
  };
}

export function createTaskMachine(task) {
  const saved = task.machineState || {};
  const state = {
    phase: saved.phase || task.phase || TASK_PHASE.DEPARTING,
    inGeofence: saved.inGeofence != null ? saved.inGeofence : !!task.inGeofence,
    geofenceMeters: GEOFENCE_METERS,
    arrivePhoto: saved.arrivePhoto || task.arrivePhoto || null,
    logs: { ...(saved.logs || task.logs || {}) },
    completed: saved.completed != null ? saved.completed : !!task.completed,
  };

  function activeStepIndex() {
    if (state.phase === TASK_PHASE.DEPARTING) return 0;
    if (state.phase === TASK_PHASE.ENROUTE) return 0;
    if (state.phase === TASK_PHASE.SERVING && !state.completed) return 3;
    if (state.completed) return 4;
    return 3;
  }

  function getProgress() {
    const idx = activeStepIndex();
    return PROGRESS_STEPS.map((step, i) => ({
      ...step,
      done: i < idx,
      active: i === idx,
    }));
  }

  function getServiceDates() {
    return eachServiceDate(task.startDate, task.endDate);
  }

  function isToday(dateKey) {
    return dateKey === formatDateKey(new Date());
  }

  function isMissed(dateKey) {
    if (isToday(dateKey)) return false;
    const log = state.logs[dateKey];
    if (!log) return true;
    return log.fed === null && log.turned === null && log.mood === null;
  }

  function isLogUnlocked(dateKey) {
    if (isToday(dateKey)) return true;
    return isMissed(dateKey);
  }

  function getLogList() {
    const dates = getServiceDates();
    const todayKey = formatDateKey(new Date());
    return dates.map((dateKey) => {
      const log = state.logs[dateKey] || defaultLogEntry();
      const missed = isMissed(dateKey);
      const today = dateKey === todayKey;
      const submitted = !!(log.submittedAt || (log.fed !== null && log.mood !== null));
      return {
        dateKey,
        displayDate: formatDisplayDate(dateKey),
        log,
        missed,
        today,
        unlocked: isLogUnlocked(dateKey),
        submitted,
        canFill: missed || today,
      };
    });
  }

  function servedDays() {
    return getServiceDates().filter((d) => {
      const log = state.logs[d];
      return log && log.fed !== null;
    }).length;
  }

  function totalDays() {
    const all = eachServiceDate(task.startDate, task.endDate);
    return all.length;
  }

  return {
    getState: () => ({ ...state }),
    getTask: () => task,
    getProgress,
    activeStepIndex,
    servedDays,
    totalDays,
    getLogList,

    startDepart() {
      if (state.phase !== TASK_PHASE.DEPARTING) return false;
      state.phase = TASK_PHASE.ENROUTE;
      state.inGeofence = false;
      return true;
    },

    simulateGeofence(enter) {
      state.inGeofence = !!enter;
    },

    canArrive() {
      return state.phase === TASK_PHASE.ENROUTE && state.inGeofence;
    },

    arriveWithPhoto(file) {
      if (!this.canArrive() || !file) return false;
      state.arrivePhoto = {
        name: file.name,
        size: file.size,
        capturedAt: Date.now(),
        preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      };
      state.phase = TASK_PHASE.SERVING;
      return true;
    },

    updateLog(dateKey, patch) {
      if (!state.logs[dateKey]) state.logs[dateKey] = defaultLogEntry();
      Object.assign(state.logs[dateKey], patch);
      if (patch.fed !== undefined || patch.turned !== undefined || patch.mood !== undefined) {
        state.logs[dateKey].submittedAt = Date.now();
      }
    },

    completeOrder() {
      if (state.phase !== TASK_PHASE.SERVING) return false;
      state.completed = true;
      return true;
    },

    serialize() {
      return {
        phase: state.phase,
        inGeofence: state.inGeofence,
        arrivePhoto: state.arrivePhoto,
        logs: state.logs,
        completed: state.completed,
      };
    },
  };
}

export function validateCameraFile(file) {
  if (!file) return { ok: false, message: "未选择照片" };
  if (!file.type.startsWith("image/")) {
    return { ok: false, message: "请使用摄像头实时拍照，不支持选择文件" };
  }
  const now = Date.now();
  if (file.lastModified && now - file.lastModified > 120000) {
    return { ok: false, message: "请现场实时拍照，请勿从相册选择旧照片" };
  }
  return { ok: true };
}
